'use client';

import { LikedFilled, NewCloseOutlined, RightArrowOutlined } from '@zeelool/icon';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import {
  dismissProductDetailFaceWidthPrompt,
  isProductDetailFaceWidthPromptDismissed,
} from '../dismissal.client';
import { requestFaceWidthMeasurement } from '../request.client';
import type {
  FaceWidthMeasurementEntrySource,
  FaceWidthMeasurementRequestHandler,
  FaceMeasurementRecord,
  ProductDetailFaceWidthFitStatus,
  ProductDetailFaceWidthResultDialogMessages,
} from '../types';

const ProductDetailFaceWidthResultDialog = dynamic(
  () => import('./ProductDetailFaceWidthResultDialog'),
  { ssr: false },
);

interface MeasurementActionProps {
  onMeasure?: FaceWidthMeasurementRequestHandler;
}

function runMeasurementRequest(
  source: FaceWidthMeasurementEntrySource,
  onMeasure?: FaceWidthMeasurementRequestHandler,
) {
  const request = { kind: 'face-width' as const, source };
  if (onMeasure) {
    onMeasure(request);
    return;
  }

  requestFaceWidthMeasurement(source);
}

interface FilterMeasurementPromptProps extends MeasurementActionProps {
  prompt: string;
  action: string;
}

export function FilterMeasurementPrompt({
  prompt,
  action,
  onMeasure,
}: FilterMeasurementPromptProps) {
  return (
    <p className="mb-[12px] text-[12px] leading-[14px] text-[#6c6c6c]">
      {prompt}{' '}
      <button
        type="button"
        className="cursor-pointer border-0 bg-transparent p-0 text-[12px] leading-[14px] text-primary-purple underline"
        onClick={() => runMeasurementRequest('goods-list-filter', onMeasure)}
      >
        {action}
      </button>
    </p>
  );
}

interface FilterRecommendationSummaryProps {
  children: string;
}

export function FilterRecommendationSummary({ children }: FilterRecommendationSummaryProps) {
  return <p className="mb-[12px] text-[12px] leading-[14px] text-[#6c6c6c]">{children}</p>;
}

interface FitsYouBadgeProps {
  label: string;
}

export function FitsYouBadge({ label }: FitsYouBadgeProps) {
  return (
    <span className="absolute right-0 top-0 flex h-[13px] w-[55px] items-center justify-center gap-[2px] rounded-bl-[6px] rounded-tr-[6px] bg-primary-purple text-[10px] font-br font-normal leading-[12px] text-white">
      <LikedFilled className="text-[9px] text-white" aria-hidden />
      {label}
    </span>
  );
}

interface ProductDetailMeasurementPromptViewProps extends MeasurementActionProps {
  prompt: string;
  action: string;
  closeLabel: string;
  onDismiss: () => void;
}

export function ProductDetailMeasurementPromptView({
  prompt,
  action,
  closeLabel,
  onDismiss,
  onMeasure,
}: ProductDetailMeasurementPromptViewProps) {
  return (
    <div className="relative mt-[8px] flex min-h-[30px] items-center rounded-[4px] bg-[#f7f7f7] px-[8px] pr-[30px] text-[12px] leading-[14px] text-[#6c6c6c]">
      <p>
        {prompt}{' '}
        <button
          type="button"
          className="cursor-pointer border-0 bg-transparent p-0 text-[12px] leading-[14px] text-primary-purple underline"
          onClick={() => runMeasurementRequest('goods-detail-size', onMeasure)}
        >
          {action}
        </button>
      </p>
      <button
        type="button"
        aria-label={closeLabel}
        className="absolute right-[6px] top-1/2 flex size-[18px] -translate-y-1/2 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-primary-black"
        onClick={onDismiss}
      >
        <NewCloseOutlined className="text-[13px]" aria-hidden />
      </button>
    </div>
  );
}

interface ProductDetailMeasurementPromptProps extends MeasurementActionProps {
  prompt: string;
  action: string;
  closeLabel: string;
}

function getBrowserStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function ProductDetailMeasurementPrompt({
  prompt,
  action,
  closeLabel,
  onMeasure,
}: ProductDetailMeasurementPromptProps) {
  const [isDismissed, setIsDismissed] = useState<boolean | null>(null);

  useEffect(() => {
    const storage = getBrowserStorage();
    setIsDismissed(storage ? isProductDetailFaceWidthPromptDismissed(storage) : false);
  }, []);

  if (isDismissed !== false) return null;

  const handleDismiss = () => {
    const storage = getBrowserStorage();
    if (storage) {
      dismissProductDetailFaceWidthPrompt(storage);
    }
    setIsDismissed(true);
  };

  return (
    <ProductDetailMeasurementPromptView
      prompt={prompt}
      action={action}
      closeLabel={closeLabel}
      onDismiss={handleDismiss}
      onMeasure={onMeasure}
    />
  );
}

interface ProductDetailResultTriggerProps extends MeasurementActionProps {
  measurement: FaceMeasurementRecord;
  dialogMessages: ProductDetailFaceWidthResultDialogMessages;
}

function useProductDetailResultDialog(onMeasure?: FaceWidthMeasurementRequestHandler) {
  const [isOpen, setIsOpen] = useState(false);

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    remeasure: () => {
      setIsOpen(false);
      runMeasurementRequest('goods-detail-size', onMeasure);
    },
  };
}

interface ProductDetailFitsYouStatusProps extends ProductDetailResultTriggerProps {
  children: string;
}

export function ProductDetailFitsYouStatus({
  children,
  measurement,
  dialogMessages,
  onMeasure,
}: ProductDetailFitsYouStatusProps) {
  const resultDialog = useProductDetailResultDialog(onMeasure);

  return (
    <>
      <button
        type="button"
        data-fit-status="fits"
        className="mt-[8px] flex w-full cursor-pointer items-center justify-between rounded-[4px] border-0 bg-[#f7f7f7] p-[8px] text-left text-[12px] leading-[14px] text-primary-purple"
        onClick={resultDialog.open}
      >
        <span className="flex items-center gap-[4px]">
          <LikedFilled className="text-[14px] text-primary-purple" aria-hidden />
          <span>{children}</span>
        </span>
        <RightArrowOutlined className="shrink-0 text-[12px] text-[#808080]" aria-hidden />
      </button>
      {resultDialog.isOpen ? (
        <ProductDetailFaceWidthResultDialog
          open
          measurement={measurement}
          messages={dialogMessages}
          onClose={resultDialog.close}
          onRemeasure={resultDialog.remeasure}
        />
      ) : null}
    </>
  );
}

interface ProductDetailSizeWarningProps extends ProductDetailResultTriggerProps {
  status: Exclude<ProductDetailFaceWidthFitStatus, 'fits'>;
  children: string;
}

export function ProductDetailSizeWarning({
  status,
  children,
  measurement,
  dialogMessages,
  onMeasure,
}: ProductDetailSizeWarningProps) {
  const resultDialog = useProductDetailResultDialog(onMeasure);

  return (
    <>
      <button
        type="button"
        data-fit-status={status}
        className="mt-[8px] flex min-h-[29px] w-full cursor-pointer items-center justify-between rounded-[4px] border-0 bg-[#f7f7f7] px-[8px] text-left text-[11px] leading-[11px] text-[#6c6c6c]"
        onClick={resultDialog.open}
      >
        <span>{children}</span>
        <RightArrowOutlined className="shrink-0 text-[12px] text-[#6c6c6c]" aria-hidden />
      </button>
      {resultDialog.isOpen ? (
        <ProductDetailFaceWidthResultDialog
          open
          measurement={measurement}
          messages={dialogMessages}
          onClose={resultDialog.close}
          onRemeasure={resultDialog.remeasure}
        />
      ) : null}
    </>
  );
}

interface SizeChartMeasurementPromptProps extends MeasurementActionProps {
  description: string;
  action: string;
}

export function SizeChartMeasurementPrompt({
  description,
  action,
  onMeasure,
}: SizeChartMeasurementPromptProps) {
  return (
    <div className="mt-[24px]">
      <p className="mb-[12px] px-[4px] text-[12px] leading-[14px] text-primary-black">
        {description}
      </p>
      <button
        type="button"
        className="flex h-[40px] w-full cursor-pointer items-center justify-center rounded-[20px] border border-[#6c6c6c] bg-white text-[14px] font-bb text-primary-black"
        onClick={() => runMeasurementRequest('goods-detail-size-chart', onMeasure)}
      >
        {action}
      </button>
    </div>
  );
}
